export const Routes = {
  // Auth routes
  LOGIN: 'login',
  REGISTER: 'register',
  START: 'start',
  LOGOUT: 'logout',
  REDIRECT_FAILURE: 'redirect-failure',
  ONBOARDING: 'onboarding',
  PRE_CHECKOUT: 'pre-checkout',
  CHECKOUT: 'checkout',

  // Main routes
  HOME: 'home',
  CONTENT: 'content',
  CHANGELOG: 'changelog',
  ASSISTANT: 'assistant',
  RESEARCH_CHAT: 'research-chat',
  IMAGE_STUDIO: 'image-studio',
  HEARTBEAT: 'heartbeat',

  EDITOR: 'editor',
  COOKINGPOT: 'cookingpot',

  // User profile routes
  PROFILE: 'profile',
  PROFILE_PRODUCTIVITY: 'profile-productivity',
  PROFILE_ORGANISATION: 'profile-organisation',
  PROFILE_FORMAT_BUILDER: 'profile-format-builder',
  PROFILE_LANGUAGE_SETTINGS: 'profile-language-settings',
  PROFILE_STYLE_SETTINGS: 'profile-style-settings',
  PROFILE_PASSWORD: 'profile-password',
  REWARDS: 'rewards',

  // Prompt routes
  PROMPTS: 'prompts',
  PROMPT_EDITOR: 'prompt-editor',
  PROMPT_CREATION: 'prompt-creation',

  // Admin routes
  ADMIN_USERS: 'admin-users',
  ADMIN_USER_DETAILS: 'admin-user-details',
  ADMIN_USER_CREATE: 'admin-user-create',
  ADMIN_ORGANISATIONS: 'admin-organisations',
  ADMIN_ORGANISATION_DETAILS: 'admin-organisation-details',
  ADMIN_ORGANISATION_CREATE: 'admin-organisation-create',
  ADMIN_CUSTOMIZATIONS: 'admin-customizations',
  ADMIN_CUSTOMIZATION_CREATE: 'admin-customization-create',
  ADMIN_CUSTOMIZATION_DETAILS: 'admin-customization-details',
  ADMIN_SCOPES: 'admin-scopes',
  ADMIN_SCOPE_CREATE: 'admin-scope-create',
  ADMIN_SCOPE_DETAILS: 'admin-scope-details',
  ADMIN_DASHBOARD: 'admin-dashboard',
  ADMIN_TPROMPTS: 'admin-tprompts',
  ADMIN_FEATURE_TEMPLATES: 'admin-tprompts',
  ADMIN_MODEL_CATALOG: 'admin-model-catalog',
  ADMIN_MODEL_CATALOG_DETAILS: 'admin-model-catalog-details',
  ADMIN_MODEL_CREATE: 'admin-model-create',
  ADMIN_MODEL_DETAILS: 'admin-model-details',
  ADMIN_FEEDS: 'admin-feeds',
  ADMIN_LIVE_CONTENT_SOURCES: 'admin-live-content-sources',
  ADMIN_LIVE_CONTENT_SOURCE_CREATE: 'admin-live-content-source-create',
  ADMIN_LIVE_CONTENT_SOURCE_DETAILS: 'admin-live-content-source-details',
  ADMIN_STATISTICS: 'admin-statistics',
  ADMIN_FORMAT_BUILDER: 'admin-format-builder',
  ADMIN_FORMAT_BUILDER_CUSTOMIZATION: 'admin-format-builder-customization',
  ADMIN_STYLE_SETTINGS: 'admin-style-settings',
  ADMIN_LANGUAGE_SETTINGS: 'admin-language-settings',
  ADMIN_ONBOARDING_FLOWS: 'admin-onboarding-flows',
  ADMIN_ONBOARDING_FLOW_CREATE: 'admin-onboarding-flow-create',
  ADMIN_ONBOARDING_FLOW_DETAILS: 'admin-onboarding-flow-details',
  ADMIN_USER_STYLES: 'admin-user-styles',
  ADMIN_USER_STYLE_DETAILS: 'admin-user-style-details',
  ADMIN_API_RESTRICTIONS: 'admin-api-restrictions',
  ADMIN_API_RESTRICTION_CREATE: 'admin-api-restriction-create',
  ADMIN_API_RESTRICTION_DETAILS: 'admin-api-restriction-details',
  ADMIN_USAGE_RESTRICTIONS: 'admin-usage-restrictions',
  ADMIN_USAGE_RESTRICTION_CREATE: 'admin-usage-restriction-create',
  ADMIN_USAGE_RESTRICTION_DETAILS: 'admin-usage-restriction-details',
  ADMIN_RESEARCH_CHAT_ANALYTICS: 'admin-research-chat-analytics',
  ADMIN_AFFILIATES: 'admin-affiliates',
  ADMIN_AFFILIATE_DETAILS: 'admin-affiliate-details',

  // Other routes
  CHIEF_EDITOR: 'chief-editor',
  USER_CHIEF_EDITOR: 'user-chief-editor',
  AUDIO_BATCH: 'audio-batch',
  NOT_AUTHORIZED: 'not-authorized',
  NOT_FOUND: 'not-found',
  TUTORIAL: 'tutorial',

  // Legal routes
  TOS: 'tos',
  PRIVACY: 'privacy',
  DPA: 'dpa',
  IMPRINT: 'imprint',
  ANLAGE1: 'anlage1',
  ANLAGE2: 'anlage2'
} as const

export type RouteName = (typeof Routes)[keyof typeof Routes]

export interface RouteQueries {
  [Routes.EDITOR]: { uuid?: string; reset?: string; entry?: string; prefillToken?: string }
  [Routes.COOKINGPOT]: { uuid?: string; reset?: string; entry?: string; prefillToken?: string }
  [Routes.LOGIN]: { redirect?: string }
  [Routes.REGISTER]: { affiliate?: string }
  [Routes.ASSISTANT]: { mode?: 'research' | 'help' }
}

export interface RouteParams {
  [Routes.PROMPT_EDITOR]: { uuid?: string }
  [Routes.ADMIN_ORGANISATION_DETAILS]: { uuid: string }
  [Routes.ADMIN_USER_DETAILS]: { uuid: string }
  [Routes.ADMIN_CUSTOMIZATION_DETAILS]: { uuid: string }
  [Routes.ADMIN_SCOPE_DETAILS]: { uuid: string }
  [Routes.ADMIN_LIVE_CONTENT_SOURCE_DETAILS]: { uuid: string }
  [Routes.ADMIN_FORMAT_BUILDER_CUSTOMIZATION]: { customizationUuid: string }
  [Routes.ADMIN_STYLE_SETTINGS]: { customizationUuid: string }
  [Routes.ADMIN_LANGUAGE_SETTINGS]: { customizationUuid: string }
  [Routes.ADMIN_ONBOARDING_FLOW_DETAILS]: { flowId: string }
  [Routes.ADMIN_USER_STYLE_DETAILS]: { userUuid: string }
  [Routes.USER_CHIEF_EDITOR]: { userUuid: string }
  [Routes.ADMIN_MODEL_DETAILS]: { uuid: string }
  [Routes.ADMIN_MODEL_CATALOG_DETAILS]: { uuid: string }
  [Routes.ADMIN_API_RESTRICTION_DETAILS]: { uuid: string }
  [Routes.ADMIN_USAGE_RESTRICTION_DETAILS]: { uuid: string }
}

export interface NavigationOptions<T extends RouteName = RouteName> {
  query?: T extends keyof RouteQueries ? RouteQueries[T] : never
  params?: T extends keyof RouteParams ? RouteParams[T] : never
  hash?: string
}
